import { useCallback, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/auth';
import { toast } from 'sonner';

export interface ShareLink {
  id: string;
  shareToken: string;
  expiresAt: string;
  viewCount: number;
  isActive: boolean;
  createdAt: string;
}

export const useReportSharing = () => {
  const { user } = useAuth();
  const [shareLinks, setShareLinks] = useState<ShareLink[]>([]);
  const [loading, setLoading] = useState(false);

  // Generate a shareable link
  const generateShareLink = useCallback(async (
    conversationId: string,
    reportData: any,
    expirationDays: number = 7
  ): Promise<string | null> => {
    if (!conversationId) {
      toast.error('No conversation to share');
      return null;
    }

    try {
      const shareToken = crypto.randomUUID();
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + expirationDays);

      // Create or update report
      const { data: report, error: reportError } = await supabase
        .from('clara_reports')
        .upsert({
          conversation_id: conversationId,
          user_id: user?.id || null,
          report_data: reportData,
          is_shared: true,
          share_token: shareToken,
          share_expires_at: expiresAt.toISOString()
        }, {
          onConflict: 'conversation_id'
        })
        .select()
        .single();

      if (reportError) throw reportError;

      const shareUrl = `${window.location.origin}/shared-report/${shareToken}`;
      
      toast.success('Share link generated');
      return shareUrl;
    } catch (error) {
      console.error('Error generating share link:', error);
      toast.error('Failed to generate share link');
      return null;
    }
  }, [user]);

  // Revoke a share link
  const revokeShareLink = useCallback(async (reportId: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('clara_reports')
        .update({
          is_shared: false,
          share_token: null,
          share_expires_at: null
        })
        .eq('id', reportId);

      if (error) throw error;

      toast.success('Share link revoked');
      return true;
    } catch (error) {
      console.error('Error revoking share link:', error);
      toast.error('Failed to revoke share link');
      return false;
    }
  }, []);

  // Get shared report by token
  const getSharedReport = useCallback(async (token: string) => {
    try {
      const { data: report, error } = await supabase
        .from('clara_reports')
        .select('*, clara_conversations(*)')
        .eq('share_token', token)
        .eq('is_shared', true)
        .single();

      if (error) throw error;

      // Check if expired
      if (report.share_expires_at && new Date(report.share_expires_at) < new Date()) {
        throw new Error('Share link has expired');
      }

      // Track view
      await supabase.from('clara_report_views').insert({
        report_id: report.id,
        share_token: token,
        viewer_ip: null, // Could be populated server-side
        viewer_user_agent: navigator.userAgent
      });

      // Increment view count
      await supabase.rpc('increment_report_view_count', { p_report_id: report.id });

      return report;
    } catch (error) {
      console.error('Error fetching shared report:', error);
      throw error;
    }
  }, []);

  // Get analytics for shared reports
  const getShareAnalytics = useCallback(async (reportId: string) => {
    try {
      const { data, error } = await supabase
        .from('clara_report_views')
        .select('*')
        .eq('report_id', reportId)
        .order('viewed_at', { ascending: false });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching share analytics:', error);
      return [];
    }
  }, []);

  return {
    shareLinks,
    loading,
    generateShareLink,
    revokeShareLink,
    getSharedReport,
    getShareAnalytics
  };
};
